import GenerateExportButton from "@app/components/generate-export-button";
import { useToast } from "@app/components/toast";
import { getAuthToken } from "@app/relay/fetch-relay";
import { environment } from "@app/relay/relay-environment";
import { Download, FileSpreadsheet, RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { exportsPage_Query } from "@app/queries/exportsPage_Query.graphql";

export const exportsQuery = graphql`
  query exportsPage_Query {
    exports {
      id
      kind
      kindLabel
      label
      status
      summary
      byteSize
      errorMessage
      createdAt
      finishedAt
      filename
      downloadPath
    }
  }
`;

export type ExportRow = exportsPage_Query["response"]["exports"][number];

// While a run is in flight the row is the only thing that moves, so poll it.
// Slow on purpose: the generators take minutes, and a one-second poll would
// re-run the whole list query sixty times to learn nothing.
const POLL_MS = 5000;

const STATUS_STYLES: Record<string, string> = {
  QUEUED: "bg-gray-100 text-gray-600",
  RUNNING: "bg-blue-50 text-blue-700",
  SUCCESS: "bg-green-50 text-green-700",
  ERROR: "bg-red-50 text-red-700",
};

const STATUS_LABELS: Record<string, string> = {
  QUEUED: "Queued",
  RUNNING: "Generating…",
  SUCCESS: "Ready",
  ERROR: "Failed",
};

const formatTimestamp = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatBytes = (bytes: number | null | undefined) => {
  if (bytes == null) return "—";
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

// Downloaded through the app rather than a bare link: the file lives in object
// storage behind an account-scoped endpoint, and the browser cannot attach the
// bearer token to a plain <a href>.
export function useDownloadExport() {
  const [downloading, setDownloading] = useState<string | null>(null);
  const toast = useToast();

  const download = async (row: {
    id: string;
    filename: string;
    downloadPath: string | null | undefined;
  }) => {
    if (!row.downloadPath) return;

    setDownloading(row.id);
    try {
      const response = await fetch(row.downloadPath, {
        headers: { Authorization: `Bearer ${getAuthToken() ?? ""}` },
      });
      if (!response.ok) {
        toast({
          message:
            response.status === 404
              ? "That file is no longer available — generate a new one."
              : "Download failed — please try again.",
          kind: "error",
        });
        return;
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = row.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ message: "Download failed — please try again.", kind: "error" });
    } finally {
      setDownloading(null);
    }
  };

  return { download, downloading };
}

// The shared list body, so a per-kind report page shows its own runs in exactly
// the shape /exports shows all of them.
export function ExportList({
  rows,
  emptyHint,
}: {
  rows: ReadonlyArray<ExportRow>;
  emptyHint: string;
}) {
  const { download, downloading } = useDownloadExport();

  if (rows.length === 0) {
    return (
      <div className="rounded border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
        <FileSpreadsheet className="mx-auto mb-2 h-6 w-6 text-gray-400" />
        <p className="text-sm text-gray-500">{emptyHint}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded border border-gray-200">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Export
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Taken
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Contents
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Status
            </th>
            <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              Size
            </th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-gray-100 align-top">
              <td className="px-3 py-2 font-medium text-gray-800">
                {row.kindLabel}
                {row.label && row.label !== row.kindLabel && (
                  <span className="block text-xs font-normal text-gray-400">
                    {row.label}
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                {formatTimestamp(row.createdAt)}
              </td>
              <td className="px-3 py-2 text-gray-500">
                {row.summary ?? "—"}
                {row.errorMessage && (
                  <span className="block text-xs text-red-600">
                    {row.errorMessage}
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2">
                <span
                  className={`rounded px-1.5 py-0.5 text-xs ${
                    STATUS_STYLES[row.status] ?? "bg-gray-100 text-gray-600"
                  }`}
                >
                  {STATUS_LABELS[row.status] ?? row.status}
                </span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-600">
                {formatBytes(row.byteSize)}
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right">
                {row.downloadPath && (
                  <button
                    type="button"
                    onClick={() => download(row)}
                    disabled={downloading === row.id}
                    className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-gray-600 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <Download className="h-3.5 w-3.5" />
                    {downloading === row.id ? "Downloading…" : "Download"}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Poll only while something is actually generating, so an idle page makes no
// requests at all. Callers pass their own refresh so the hook works for both the
// whole list and a per-kind view.
export function usePollWhilePending(
  pending: boolean,
  refresh: () => void,
  intervalMs = POLL_MS
) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    if (!pending) return;

    const timer = window.setInterval(() => refreshRef.current(), intervalMs);
    return () => window.clearInterval(timer);
  }, [pending, intervalMs]);
}

export const isPending = (rows: ReadonlyArray<{ status: string }>) =>
  rows.some((row) => row.status === "QUEUED" || row.status === "RUNNING");

export default function ExportsPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<exportsPage_Query>;
}) {
  const preloaded = usePreloadedQuery(exportsQuery, queryRef);
  const [data, setData] = useState(preloaded);

  // Re-sync the local mirror only when the route hands a NEW query, keyed on
  // queryRef — NOT on `preloaded`: refresh() writes the same query root into the
  // store, and keying on `preloaded` would re-emit the stale snapshot and clobber
  // what the poll just fetched.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);

  const refresh = () =>
    fetchQuery<exportsPage_Query>(
      environment,
      exportsQuery,
      {},
      { fetchPolicy: "network-only" }
    )
      .toPromise()
      .then((fresh) => {
        if (fresh) setData(fresh);
      });

  const rows = data.exports;
  usePollWhilePending(isPending(rows), refresh);

  return (
    <div className="mx-auto max-w-full space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-bold">Exports</h1>
        <div className="ml-auto flex items-center gap-2">
          {/* Stays on this page: the queued run appears in the list below. */}
          <GenerateExportButton
            kind="VC_DATACUBE"
            label="Generate VC datacube"
            onQueued={() => refresh()}
          />
          <button
            type="button"
            onClick={() => refresh()}
            className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </div>
      </div>

      <p className="max-w-3xl text-sm text-gray-500">
        Every workbook this account has produced, newest first — reports
        generated in the background, and the grids exported from a screen. They
        stay here after the tab closes, so a file can be fetched again without
        re-running it.
      </p>

      <ExportList
        rows={rows}
        emptyHint="Nothing exported yet. Generate a report, or use “Export to Excel” on any data screen."
      />
    </div>
  );
}
